import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { useSlotsGame } from './hooks/useSlotsGame'
import { SlotMachine } from './components/SlotMachine'
import { Controls } from './components/Controls'
import { PayTable } from './components/PayTable'
import { SYMBOLS } from './utils/slotsLogic'

export function Slots() {
  const { user, isDemo } = useAuth()
  const [showPayTable, setShowPayTable] = useState(false)

  const {
    visibleSymbols,
    activePaylines,
    betPerLine,
    totalBet,
    phase,
    wins,
    freeSpinsRemaining,
    isSpinning,
    lastWin,
    setPaylines,
    setBetPerLine,
    spin,
  } = useSlotsGame()

  // Use correct balance based on user type
  const balance = isDemo ? (user?.demoBalance || 0) : (user?.balance || 0)

  // Check if can spin
  const canSpin = !isSpinning && (freeSpinsRemaining > 0 || totalBet <= balance)

  return (
    <div
      className={cn(
        'flex-1 flex flex-col',
        'bg-gradient-to-b from-casino-black via-gray-900 to-casino-black',
        'min-h-screen'
      )}
    >
      {/* Header con free spins */}
      {freeSpinsRemaining > 0 && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center py-3 bg-gradient-to-r from-green-600/50 via-green-500/50 to-green-600/50"
        >
          <span className="text-xl font-bold text-white">
            🎁 FREE SPINS: {freeSpinsRemaining} restantes
          </span>
        </motion.div>
      )}

      {/* Área principal del juego */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 md:p-8">
        {/* Máquina de slots */}
        <SlotMachine
          visibleSymbols={visibleSymbols}
          isSpinning={isSpinning}
          wins={wins}
          activePaylines={activePaylines}
          lastWin={lastWin}
          onPullLever={spin}
          canSpin={canSpin}
        />

        {/* Detalle de líneas ganadoras - Solo una vez, debajo de la máquina */}
        <AnimatePresence>
          {wins.length > 0 && phase === 'result' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-4 flex flex-wrap justify-center gap-2"
            >
              {wins.map((win, i) => (
                <div
                  key={i}
                  className="px-3 py-1 rounded-full bg-gray-800/80 text-sm"
                >
                  <span className="text-gray-400">L{win.paylineIndex + 1}:</span>{' '}
                  <span className="text-xl">{SYMBOLS[win.symbol].emoji}</span>{' '}
                  <span className="text-white">×{win.count}</span>{' '}
                  <span className="text-casino-gold font-bold">${win.payout}</span>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Controles */}
      <Controls
        balance={balance}
        betPerLine={betPerLine}
        activePaylines={activePaylines}
        totalBet={totalBet}
        isSpinning={isSpinning}
        freeSpinsRemaining={freeSpinsRemaining}
        onBetChange={setBetPerLine}
        onPaylinesChange={setPaylines}
        onShowPayTable={() => setShowPayTable(true)}
      />

      {/* Modal de tabla de pagos */}
      <PayTable
        betPerLine={betPerLine}
        isOpen={showPayTable}
        onClose={() => setShowPayTable(false)}
      />
    </div>
  )
}
